import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SiteContentService, HomeAssets } from '../../../services/site-content.service';
import { LanguageService, Language } from '../../../services/language.service';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase.config';

@Component({
  selector: 'app-admin-site-content',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './site-content.html',
  styleUrls: ['./site-content.scss']
})
export class AdminSiteContentComponent implements OnInit {
  loading = false;
  saving = false;
  uploading = false;
  assets: HomeAssets | null = null;

  bannerPreview: string = '';
  offerPreview: string = '';

  bannerUploadProgress = 0;
  offerUploadProgress = 0;
  uploadProgress = 0;

  deleting: { [key: string]: boolean } = {};

  languages: { code: string; name: string }[] = [];
  selectedLang: string = 'en';

  // Form data
  formData = {
    imageType: '',
    language: 'en'
  };
  selectedFile: File | null = null;

  constructor(private siteContent: SiteContentService, private languageService: LanguageService) {}

  ngOnInit(): void {
    this.languageService.languages$.subscribe(list => {
      this.languages = (list || []).map(l => ({ code: l.code, name: l.name }));
    });
    this.refresh();
  }

  refresh() {
    this.loading = true;
    this.siteContent.getHomeAssets().subscribe({
      next: async (data) => {
        this.assets = data;
        this.updatePreviewsForSelectedLang();
        // Run migration to convert 'default' to 'en' if needed
        await this.siteContent.migrateDefaultToEnglish();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onLanguageChange() {
    this.updatePreviewsForSelectedLang();
  }

  private updatePreviewsForSelectedLang() {
    const lang = this.selectedLang;
    const bannerUrl = this.assets?.banner?.[lang]?.url || '';
    const offerUrl = this.assets?.offers?.[lang]?.[0]?.url || ''; // Get first offer
    this.bannerPreview = bannerUrl;
    this.offerPreview = offerUrl;
  }

  async deleteImage(type: 'banner' | 'offer', lang: string, imageData: any, offerIndex?: number) {
    const confirmMessage = type === 'offer' 
      ? `Delete this offer image #${(offerIndex || 0) + 1} for ${lang}? This action cannot be undone.`
      : `Delete this ${type} image for ${lang}? This action cannot be undone.`;
      
    if (!confirm(confirmMessage)) {
      return;
    }

    const key = type === 'offer' ? `${lang}_${type}_${offerIndex}` : `${lang}_${type}`;
    this.deleting[key] = true;

    try {
      // Delete from Firebase Storage
      await this.siteContent.fileUploadService.deleteFile(imageData.path).toPromise();
      
      if (type === 'banner') {
        // Remove banner from Firestore
        const ref = doc(db, 'siteContent', 'home');
        const path = `${type}.${lang}` as any;
        await updateDoc(ref, { [path]: null } as any);
      } else if (type === 'offer' && offerIndex !== undefined) {
        // Remove specific offer from array
        await this.siteContent.deleteOfferForLanguage(lang, offerIndex);
      }
      
      // Refresh the display
      this.refresh();
    } catch (error) {
      alert('Error deleting image. Please try again.');
    } finally {
      this.deleting[key] = false;
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  removeFile() {
    this.selectedFile = null;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  canSubmit(): boolean {
    return !!(this.formData.imageType && this.formData.language && this.selectedFile);
  }

  onFormSubmit() {
    if (!this.canSubmit()) return;
    
    this.uploading = true;
    this.uploadProgress = 0;
    
    const uploadMethod = this.formData.imageType === 'banner' 
      ? this.siteContent.uploadBannerWithProgress(this.selectedFile!)
      : this.siteContent.uploadOfferWithProgress(this.selectedFile!);
    
    uploadMethod.subscribe(({ result, progress }) => {
      this.uploadProgress = progress.percentage || 0;
      
      if (result.success && result.downloadURL && result.filePath) {
        const lang = this.formData.language;
        const saveMethod = this.formData.imageType === 'banner'
          ? this.siteContent.saveBannerForLanguage(lang, { url: result.downloadURL, path: result.filePath })
          : this.siteContent.saveOfferForLanguage(lang, { url: result.downloadURL, path: result.filePath });
        
        saveMethod
          .then(() => {
            this.refresh();
            this.resetForm();
          })
          .finally(() => {
            this.uploading = false;
            this.uploadProgress = 0;
          });
      }
    });
  }

  resetForm() {
    this.formData = {
      imageType: '',
      language: 'en'
    };
    this.selectedFile = null;
    this.uploadProgress = 0;
  }

  private async save(partial: Partial<HomeAssets>) {
    this.saving = true;
    await this.siteContent.saveHomeAssets(partial);
    this.saving = false;
  }
}


